import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { CardTemplate, CardSize, FontSize, type CardCustomizationController } from '@/hooks/useCardCustomization'
import { AdvancedCardConfigModal } from './AdvancedCardConfigModal'
import { useTranslation } from '@/contexts/TranslationContext'

// Ícones SVG
const Icons = {
  Settings: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  Eye: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  EyeOff: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ),
  RotateCcw: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  ),
  GripVertical: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="5" r="1" />
      <circle cx="9" cy="12" r="1" />
      <circle cx="9" cy="19" r="1" />
      <circle cx="15" cy="5" r="1" />
      <circle cx="15" cy="12" r="1" />
      <circle cx="15" cy="19" r="1" />
    </svg>
  ),
  Check: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  LayoutGrid: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  ),
  Image: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  ),
  List: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  ),
  Type: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="4 7 4 4 20 4 20 7" />
      <line x1="9" y1="20" x2="15" y2="20" />
      <line x1="12" y1="4" x2="12" y2="20" />
    </svg>
  )
}

interface CardCustomizationMenuProps {
  fields: Array<{
    key: string
    label: string
    visible: boolean
  }>
  template: CardTemplate
  size: CardSize
  titleFontSize: FontSize
  subtitleFontSize: FontSize
  onToggleField: (key: string) => void
  onReorderFields: (fromIndex: number, toIndex: number) => void
  onChangeTemplate: (template: CardTemplate) => void
  onChangeSize: (size: CardSize) => void
  onChangeTitleFontSize: (size: FontSize) => void
  onChangeSubtitleFontSize: (size: FontSize) => void
  onReset: () => void
  controller?: CardCustomizationController  // Opcional para modo avançado
}

export function CardCustomizationMenu({
  fields,
  template,
  size,
  titleFontSize,
  subtitleFontSize,
  onToggleField,
  onReorderFields,
  onChangeTemplate,
  onChangeSize,
  onChangeTitleFontSize,
  onChangeSubtitleFontSize,
  onReset,
  controller
}: CardCustomizationMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [showAdvancedModal, setShowAdvancedModal] = useState(false)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const dragOverIndex = useRef<number | null>(null)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const handleDragStart = (index: number) => {
    setDraggedIndex(index)
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    dragOverIndex.current = index
  }

  const handleDragEnd = () => {
    if (draggedIndex !== null && dragOverIndex.current !== null && draggedIndex !== dragOverIndex.current) {
      onReorderFields(draggedIndex, dragOverIndex.current)
    }
    setDraggedIndex(null)
    dragOverIndex.current = null
  }

  const { t } = useTranslation()

  // Grouping controls (kanban)
  const grouping = controller?.settings.grouping || { mode: 'normal' as const }
  const [localGroupingMode, setLocalGroupingMode] = useState<'normal' | 'kanban'>(grouping.mode || 'normal')
  const [localGroupingField, setLocalGroupingField] = useState<string | undefined>(grouping.fieldKey)
  const [localGroupingColumnsText, setLocalGroupingColumnsText] = useState<string>((grouping.columns || []).join(','))

  const templateOptions: Array<{ value: CardTemplate; label: string; description: string; icon: React.ReactNode }> = [
    { 
      value: 'compact', 
      label: t('customization.card.template.compact.label'), 
      description: t('customization.card.template.compact.description'),
      icon: <Icons.LayoutGrid className="w-4 h-4" />
    },
    { 
      value: 'image-focus', 
      label: t('customization.card.template.image-focus.label'), 
      description: t('customization.card.template.image-focus.description'),
      icon: <Icons.Image className="w-4 h-4" />
    },
    { 
      value: 'details-rich', 
      label: t('customization.card.template.details-rich.label'), 
      description: t('customization.card.template.details-rich.description'),
      icon: <Icons.List className="w-4 h-4" />
    },
    {
      value: 'list-view',
      label: t('customization.card.template.list-view.label'),
      description: t('customization.card.template.list-view.description'),
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      )
    }
  ]

  const sizeOptions: Array<{ value: CardSize; label: string; description: string }> = [
    { value: 'small', label: t('customization.card.size.small.label'), description: t('customization.card.size.small.description') },
    { value: 'medium', label: t('customization.card.size.medium.label'), description: t('customization.card.size.medium.description') },
    { value: 'large', label: t('customization.card.size.large.label'), description: t('customization.card.size.large.description') }
  ]

  const fontSizeOptions: Array<{ value: FontSize; label: string }> = [
    { value: 'xs', label: t('customization.card.fontSizes.xs') },
    { value: 'sm', label: t('customization.card.fontSizes.sm') },
    { value: 'base', label: t('customization.card.fontSizes.base') },
    { value: 'lg', label: t('customization.card.fontSizes.lg') },
    { value: 'xl', label: t('customization.card.fontSizes.xl') }
  ]

  // ── conteúdo das seções (reutilizado no desktop e no mobile) ──────────────
  const menuSections = (
    <>
              {/* Seção de Templates */}
              <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Icons.LayoutGrid className="w-3.5 h-3.5" />
                  {t('customization.card.template.title')}
                </h4>
                <div className="space-y-2">
                  {templateOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => onChangeTemplate(option.value)}
                      className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-md transition-colors ${
                        template === option.value
                          ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-700/50 border border-transparent'
                      }`}
                    >
                      <div className={`mt-0.5 ${template === option.value ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400'}`}>
                        {option.icon}
                      </div>
                      <div className="text-left flex-1">
                        <div className={`text-sm font-medium ${template === option.value ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-700 dark:text-slate-300'}`}>
                          {option.label}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {option.description}
                        </div>
                      </div>
                      {template === option.value && (
                        <Icons.Check className="w-4 h-4 mt-0.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Seção de Tamanho */}
              <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                  {t('customization.card.size.title')}
                </h4>
                <div className="space-y-1">
                  {sizeOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => onChangeSize(option.value)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-md transition-colors ${
                        size === option.value
                          ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-700/50 text-slate-700 dark:text-slate-300'
                      }`}
                    >
                      <div className="text-left">
                        <div className="text-sm font-medium">{option.label}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {option.description}
                        </div>
                      </div>
                      {size === option.value && (
                        <Icons.Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Seção de Fontes */}
              <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Icons.Type className="w-3.5 h-3.5" />
                  {t('customization.card.fontSizes.title')}
                </h4>
                
                {/* Título */}
                <div className="mb-3">
                  <label className="text-xs text-slate-600 dark:text-slate-400 mb-1.5 block">{t('customization.card.fontSizes.titleLabel')}</label>
                  <select
                    value={titleFontSize}
                    onChange={(e) => onChangeTitleFontSize(e.target.value as FontSize)}
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 rounded-md focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 focus:border-transparent"
                  >
                    {fontSizeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Subtítulo */}
                <div>
                  <label className="text-xs text-slate-600 dark:text-slate-400 mb-1.5 block">{t('customization.card.fontSizes.subtitleLabel')}</label>
                  <select
                    value={subtitleFontSize}
                    onChange={(e) => onChangeSubtitleFontSize(e.target.value as FontSize)}
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 rounded-md focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 focus:border-transparent"
                  >
                    {fontSizeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Botão de Configuração Avançada */}
              {controller && (
                <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                  <button
                    onClick={() => {
                      setShowAdvancedModal(true)
                      setIsOpen(false)
                    }}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-all bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600 text-white shadow-md hover:shadow-lg"
                  >
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                      </svg>
                      <div className="text-left">
                        <div className="font-semibold text-sm">⚙️ {t('customization.card.advanced.title')}</div>
                        <div className="text-xs opacity-90">
                          {t('customization.card.advanced.subtitle')}
                        </div>
                      </div>
                    </div>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  {controller.settings.advanced?.enabled && (
                    <div className="mt-2 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-md border border-emerald-200 dark:border-emerald-800">
                      <p className="text-xs text-emerald-700 dark:text-emerald-300 font-medium">
                        ✓ {t('customization.card.advanced.enabledBadge')}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Seção de Campos */}
              <div className="px-4 py-3 pb-24">
                {/* Seção de Agrupamento / Kanban */}
                {controller && (
                  <div className="mb-4">
                    <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                      {t('customization.card.grouping.title') || 'Organização'}
                    </h4>
                    <div className="flex items-center gap-2 mb-2">
                      <label className={`px-2 py-1 rounded ${localGroupingMode === 'normal' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
                        <input type="radio" name="groupingMode" checked={localGroupingMode === 'normal'} onChange={() => setLocalGroupingMode('normal')} /> Normal
                      </label>
                      <label className={`px-2 py-1 rounded ${localGroupingMode === 'kanban' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
                        <input type="radio" name="groupingMode" checked={localGroupingMode === 'kanban'} onChange={() => setLocalGroupingMode('kanban')} /> Kanban
                      </label>
                    </div>

                    {localGroupingMode === 'kanban' && (
                      <div className="space-y-2">
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Campo para agrupar</label>
                          <select
                            value={localGroupingField}
                            onChange={(e) => setLocalGroupingField(e.target.value || undefined)}
                            className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 rounded-md"
                          >
                            <option value="">-- selecionar --</option>
                            {fields.map(f => (
                              <option key={f.key} value={f.key}>{f.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Colunas (valores separados por vírgula)</label>
                          <input
                            type="text"
                            value={localGroupingColumnsText}
                            onChange={(e) => setLocalGroupingColumnsText(e.target.value)}
                            placeholder="Ativo,Inativo"
                            className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 rounded-md"
                          />
                        </div>

                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => {
                              // Apply to controller
                              controller.setGroupingMode(localGroupingMode)
                              controller.setGroupingField(localGroupingField)
                              const cols = localGroupingColumnsText.split(',').map(s => s.trim()).filter(Boolean)
                              controller.setGroupingColumns(cols)
                              setIsOpen(false)
                            }}
                            className="px-3 py-2 bg-emerald-600 text-white rounded-md"
                          >
                            {t('customization.card.grouping.apply') || 'Aplicar'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                  {t('customization.card.fields.title')}
                </h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-3 italic">
                  {fields.length > 0 ? t('customization.card.fields.dragHint') : t('customization.card.fields.noFields')}
                </p>
                {fields.length === 0 ? (
                  <div className="text-center py-8 px-4">
                    <div className="text-slate-400 dark:text-slate-500 mb-2">
                      <Icons.List className="w-8 h-8 mx-auto opacity-50" />
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {t('customization.card.fields.configureCode')}
                    </p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                      {t('customization.card.fields.subtitle')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1">
                  {fields.map((field, index) => (
                    <div
                      key={field.key}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`flex items-center gap-2 px-2 py-2 rounded-md cursor-move transition-colors ${
                        draggedIndex === index
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 opacity-50'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                      }`}
                    >
                      {/* Ícone de drag */}
                      <Icons.GripVertical className="w-4 h-4 text-slate-400 dark:text-slate-500 flex-shrink-0" />

                      {/* Nome do campo */}
                      <span className="flex-1 text-sm text-slate-700 dark:text-slate-300">
                        {field.label}
                      </span>

                      {/* Botão de visibilidade */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          onToggleField(field.key)
                        }}
                        className={`flex-shrink-0 p-1 rounded transition-colors ${
                          field.visible
                            ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
                            : 'text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                        title={field.visible ? t('customization.card.hideField') : t('customization.card.showField')}
                      >
                        {field.visible ? (
                          <Icons.Eye className="w-4 h-4" />
                        ) : (
                          <Icons.EyeOff className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  ))}
                  </div>
                )}
              </div>
    </>
  )
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="relative">
      {/* Botão de abrir menu */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2.5 rounded-lg transition-colors bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700"
        title={t('customization.card.title')}
      >
        <Icons.Settings className="w-5 h-5" />
      </button>

      {/* Mobile: tela cheia com seta de voltar */}
      {isOpen && isMobile && createPortal(
        <div className="fixed inset-0 z-[9999] bg-white dark:bg-slate-900 flex flex-col">
          {/* Header mobile */}
          <div className="flex items-center gap-3 px-4 py-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-emerald-50 to-slate-50 dark:from-emerald-900/10 dark:to-slate-900">
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label="Fechar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h3 className="flex-1 text-base font-semibold text-slate-900 dark:text-slate-100">
              ✨ {t('customization.card.title')}
            </h3>
            <button
              onClick={onReset}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
              title={t('customization.card.restore')}
            >
              <Icons.RotateCcw className="w-3.5 h-3.5" />
              <span>{t('customization.card.restore')}</span>
            </button>
          </div>
          {/* Conteúdo scrollável */}
          <div className="flex-1 overflow-y-auto">
            {menuSections}
          </div>
        </div>,
        document.body
      )}

      {/* Desktop: dropdown posicionado */}
      {isOpen && !isMobile && (
        <>
          {/* Overlay para fechar ao clicar fora */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          {/* Conteúdo do menu */}
          <div className="absolute right-0 mt-2 w-96 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-50 overflow-hidden">
            {/* Cabeçalho */}
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-slate-50 dark:from-emerald-900/10 dark:to-slate-900">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                ✨ {t('customization.card.title')}
              </h3>
              <button
                onClick={onReset}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                title={t('customization.card.restore')}
              >
                <Icons.RotateCcw className="w-3 h-3" />
                <span>{t('customization.card.restore')}</span>
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {menuSections}
            </div>
          </div>
        </>
      )}

      {/* Advanced Configuration Modal */}
      {controller && (
        <AdvancedCardConfigModal
          isOpen={showAdvancedModal}
          onClose={() => setShowAdvancedModal(false)}
          controller={controller}
          availableFields={fields.map(f => ({ key: f.key, label: f.label }))}
        />
      )}
    </div>
  )
}
